import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import API from "@/lib/api";
import {
  Copy, Check, Search, Filter, Library, Shield, TestTube,
  FileText, Archive, Loader2, ExternalLink, Upload, Sparkles,
  Trash2, FolderOpen, X, Lightbulb, Star, Tag, ChevronDown, ChevronRight,
  ArrowUpDown, SortAsc, SortDesc, Clock, MoreHorizontal, ClipboardCheck,
  Merge, AlertTriangle, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { StatBar } from "@/components/shared/StatBar";
import { DialogBody } from "@/components/shared/DialogBody";
import { ImportDialog } from "@/features/library/ImportDialog";
import { AuditDialog } from "@/features/library/AuditDialog";
import { SuggestDialog } from "@/features/library/SuggestDialog";
import { LibraryAssistant } from "@/features/library/LibraryAssistant";

const STAGE_TABS = [
  { value: "all", label: "All", icon: Library },
  { value: "draft", label: "Draft", icon: FileText },
  { value: "tested", label: "Tested", icon: TestTube },
  { value: "production", label: "Production", icon: Shield },
  { value: "deprecated", label: "Deprecated", icon: Archive },
];

const MODE_FILTERS = ["all", "define", "shape", "implement", "verify", "iterate"];
const SOURCE_FILTERS = [
  { value: "", label: "Library Blocks" },
  { value: "project", label: "Playbook Blocks" },
];
const BUILD_TYPES = [
  { value: "general", label: "General" },
  { value: "app", label: "App" },
  { value: "website", label: "Website" },
  { value: "saas", label: "SaaS" },
  { value: "api", label: "API" },
];

const CATEGORY_TAGS = [
  "Product", "Information Architecture", "UX", "UI", "Accessibility",
  "Frontend", "Backend", "Business Logic", "State Management", "Data",
  "API", "Integrations", "Authentication & Authorization", "Security",
  "Error Handling", "Performance", "Testing", "Analytics", "Content",
  "SEO", "DevOps", "Monitoring & Reliability", "Compliance",
  "Billing & Subscriptions", "Admin & Operations"
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First", icon: Clock },
  { value: "oldest", label: "Oldest First", icon: Clock },
  { value: "a-z", label: "A → Z", icon: SortAsc },
  { value: "z-a", label: "Z → A", icon: SortDesc },
];

const STAGE_COLORS = {
  draft: "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)]",
  tested: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  production: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  deprecated: "bg-[var(--pf-surface)] text-[var(--pf-text-muted)] line-through",
};

const SOURCE_BADGES = {
  imported: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  promoted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  suggested: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  pack: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  project: "bg-[var(--pf-surface)] text-[var(--pf-text-muted)]",
};

const QUALITY_BORDERS = {
  template: "border-l-[var(--pf-border)]",
  adapted: "border-l-[var(--pf-accent)]",
  sharpened: "border-l-[#16A34A]",
  edited: "border-l-[#EAB308]",
  imported: "border-l-[#9333EA]",
  promoted: "border-l-[#2563EB]",
  pack: "border-l-[#0D9488]",
};

export default function LibraryPage() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read initial state from URL
  const [stageFilter, setStageFilter] = useState(searchParams.get("stage") || "all");
  const [modeFilter, setModeFilter] = useState(searchParams.get("mode") || "all");
  const [tagFilters, setTagFilters] = useState(() => {
    const t = searchParams.get("tags");
    if (!t) return new Set();
    // Strip tags that are not in the canonical 25-category taxonomy
    const known = new Set(CATEGORY_TAGS);
    return new Set(t.split(",").filter((x) => known.has(x)));
  });
  const [tagMatchAll, setTagMatchAll] = useState(searchParams.get("tagmatch") === "all");
  const [sourceFilter, setSourceFilter] = useState(searchParams.get("source") || "");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [selectedBlocks, setSelectedBlocks] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);
  const [showFavorites, setShowFavorites] = useState(searchParams.get("fav") === "1");
  const [showFilters, setShowFilters] = useState(true);
  const [sortOrder, setSortOrder] = useState(searchParams.get("sort") || "newest");
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingBlock, setEditingBlock] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", content: "", mode: "", tags: "" });
  const [editSaving, setEditSaving] = useState(false);

  // Library Assistant (P1.2): single right-rail surface that subsumes Audit + Suggest + Import.
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantPanel, setAssistantPanel] = useState(null); // "detect" | "discover" | "ingest"
  const [suggestInitial, setSuggestInitial] = useState({ mode: "", category: "" });

  useEffect(() => { loadLibrary(); }, [sourceFilter, showFavorites]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle ?action= deep-link from Packs page or Coverage matrix click-to-suggest
  useEffect(() => {
    const action = searchParams.get("action");
    if (!action) return;
    const ACTION_TO_PANEL = { import: "ingest", suggest: "discover", audit: "detect" };
    const panel = ACTION_TO_PANEL[action];
    if (panel) {
      if (action === "suggest") {
        // Prefilled mode/category are used ONLY by the Discover panel — don't leak
        // into the Library filter URL state. Snapshot & strip them here.
        const preMode = searchParams.get("mode") || "";
        const preCategory = searchParams.get("category") || "";
        setSuggestInitial({ mode: preMode, category: preCategory });
      }
      setAssistantPanel(panel);
      setAssistantOpen(true);
    }
    // Clear the action (+ suggest-only params) so URL-sync effect doesn't fight with it
    const p = new URLSearchParams(searchParams);
    p.delete("action");
    if (action === "suggest") { p.delete("mode"); p.delete("category"); }
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filters to URL
  useEffect(() => {
    const p = new URLSearchParams();
    if (modeFilter !== "all") p.set("mode", modeFilter);
    if (tagFilters.size > 0) p.set("tags", [...tagFilters].join(","));
    if (tagMatchAll && tagFilters.size > 1) p.set("tagmatch", "all");
    if (sourceFilter) p.set("source", sourceFilter);
    if (searchQuery) p.set("q", searchQuery);
    if (showFavorites) p.set("fav", "1");
    if (sortOrder !== "newest") p.set("sort", sortOrder);
    if (stageFilter !== "all") p.set("stage", stageFilter);
    setSearchParams(p, { replace: true });
  }, [modeFilter, tagFilters, tagMatchAll, sourceFilter, searchQuery, showFavorites, sortOrder, stageFilter, setSearchParams]);

  const resetAllFilters = () => {
    setModeFilter("all");
    setTagFilters(new Set());
    setTagMatchAll(false);
    setSourceFilter("");
    setSearchQuery("");
    setShowFavorites(false);
    setStageFilter("all");
  };

  const loadLibrary = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (sourceFilter) params.source = sourceFilter;
      if (showFavorites) params.favorites = true;
      const { data } = await API.get("/library", { params });
      setBlocks(data.items || data);
    } catch (e) {
      console.error("Library load error:", e);
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  const filteredBlocks = useMemo(() => {
    let result = blocks.filter((b) => {
      if (stageFilter !== "all" && (b.stage || "draft") !== stageFilter) return false;
      if (modeFilter !== "all" && b.mode !== modeFilter) return false;
      if (tagFilters.size > 0) {
        const blockTags = b.tags || [];
        if (tagMatchAll) {
          // ALL selected tags must be present on the block
          for (const t of tagFilters) { if (!blockTags.includes(t)) return false; }
        } else {
          // ANY selected tag is enough (legacy default)
          if (!blockTags.some((t) => tagFilters.has(t))) return false;
        }
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          (b.title || "").toLowerCase().includes(q) ||
          (b.content || "").toLowerCase().includes(q) ||
          (b.tags || []).some((t) => t.toLowerCase().includes(q))
        );
      }
      return true;
    });
    // Sort
    result = [...result].sort((a, b) => {
      switch (sortOrder) {
        case "oldest": return (a.created_at || "").localeCompare(b.created_at || "");
        case "a-z": return (a.title || "").localeCompare(b.title || "");
        case "z-a": return (b.title || "").localeCompare(a.title || "");
        case "newest":
        default: return (b.created_at || "").localeCompare(a.created_at || "");
      }
    });
    return result;
  }, [blocks, stageFilter, modeFilter, tagFilters, tagMatchAll, searchQuery, sortOrder]);

  const stageCounts = useMemo(() => {
    const counts = { all: blocks.length, draft: 0, tested: 0, production: 0, deprecated: 0 };
    blocks.forEach((b) => { const s = b.stage || "draft"; if (counts[s] !== undefined) counts[s]++; });
    return counts;
  }, [blocks]);

  // Mode counts are intersected with stage + tag filters so users see "how many
  // blocks would this mode surface given my other active filters".
  const modeCounts = useMemo(() => {
    const counts = {};
    blocks.forEach((b) => {
      if (stageFilter !== "all" && (b.stage || "draft") !== stageFilter) return;
      if (tagFilters.size > 0) {
        const blockTags = b.tags || [];
        if (tagMatchAll) {
          for (const t of tagFilters) { if (!blockTags.includes(t)) return; }
        } else {
          if (!blockTags.some((t) => tagFilters.has(t))) return;
        }
      }
      const m = b.mode || "unknown";
      counts[m] = (counts[m] || 0) + 1;
    });
    return counts;
  }, [blocks, stageFilter, tagFilters, tagMatchAll]);

  // Tag counts are intersected with stage + mode so category chips reflect the
  // current context.
  const tagCounts = useMemo(() => {
    const counts = {};
    blocks.forEach((b) => {
      if (stageFilter !== "all" && (b.stage || "draft") !== stageFilter) return;
      if (modeFilter !== "all" && b.mode !== modeFilter) return;
      (b.tags || []).forEach((t) => { counts[t] = (counts[t] || 0) + 1; });
    });
    return counts;
  }, [blocks, stageFilter, modeFilter]);

  // Baseline counts (ignoring every filter) — used for "All" pills so users can
  // always see total library breadth regardless of current narrowing.
  const modeCountsTotal = useMemo(() => {
    const counts = {};
    blocks.forEach((b) => { const m = b.mode || "unknown"; counts[m] = (counts[m] || 0) + 1; });
    return counts;
  }, [blocks]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (stageFilter !== "all") c++;
    if (modeFilter !== "all") c++;
    if (tagFilters.size > 0) c++;
    if (sourceFilter) c++;
    if (showFavorites) c++;
    return c;
  }, [stageFilter, modeFilter, tagFilters, sourceFilter, showFavorites]);

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
  };

  const handleCopyBlock = async (block, uid) => {
    await copyToClipboard(block.content);
    setCopiedId(uid);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleCopySelected = async () => {
    const selected = filteredBlocks.filter((_, i) => selectedBlocks.has(i));
    if (selected.length === 0) return;
    const text = selected.map((b) => `--- ${b.title} ---\n${b.content}`).join("\n\n");
    await copyToClipboard(text);
    toast.success(`Copied ${selected.length} blocks`);
    setSelectedBlocks(new Set());
  };

  const toggleSelect = (idx) => {
    setSelectedBlocks((prev) => { const n = new Set(prev); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });
  };

  const handleSelectAll = () => {
    setSelectedBlocks(selectedBlocks.size === filteredBlocks.length ? new Set() : new Set(filteredBlocks.map((_, i) => i)));
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await API.delete(`/library/blocks/${blockId}`);
      setBlocks((prev) => prev.filter((b) => b.id !== blockId));
      setDeleteConfirm(null);
      toast.success("Block deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleToggleFavorite = async (blockId) => {
    try {
      const { data } = await API.post(`/library/blocks/${blockId}/favorite`);
      setBlocks((prev) => prev.map((b) => b.id === blockId ? { ...b, is_favorite: data.is_favorite } : b));
    } catch (e) {
      toast.error("Failed to update favorite");
    }
  };

  // --- Import ---

  // --- Suggest ---

  // --- Packs ---
  const toggleTagFilter = useCallback((tag) => {
    setTagFilters((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const handleEditBlock = async () => {
    if (!editingBlock) return;
    setEditSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        content: editForm.content.trim(),
        mode: editForm.mode,
        tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await API.put(`/library/blocks/${editingBlock}`, payload);
      setBlocks((prev) => prev.map((b) => b.id === editingBlock ? { ...b, ...payload } : b));
      setEditingBlock(null);
      toast.success("Block updated");
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  const startEditing = (block, uid) => {
    setEditingBlock(block.id);
    setEditForm({ title: block.title || "", content: block.content || "", mode: block.mode || "implement", tags: (block.tags || []).join(", ") });
    setExpandedBlock(uid);
  };

  const handleStageChange = async (blockId, newStage) => {
    try {
      await API.put(`/library/blocks/${blockId}/stage`, { stage: newStage });
      setBlocks((prev) => prev.map((b) => b.id === blockId ? { ...b, stage: newStage } : b));
      toast.success(`Stage → ${newStage}`);
    } catch (e) {
      toast.error("Stage update failed");
    }
  };


  // --- Audit ---

  const getBlockUid = (block, idx) => {
    if (block.standalone && block.id) return `lib-${block.id}`;
    return `${block.prompt_id || "x"}-${block.mode}-${block.index ?? idx}`;
  };

  return (
    <div className="h-screen flex flex-col bg-[var(--pf-bg)]" data-testid="library-page">
      <Header />
      {/* Fixed top area */}
      <div className="shrink-0 max-w-[1400px] w-full mx-auto px-6">
        {/* Page header */}
        <div className="flex items-center justify-between py-3">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[var(--pf-text)]" data-testid="library-title">Prompt Library</h1>
          <div className="flex items-center gap-2">
            <LibraryAssistant
              open={assistantOpen}
              onOpenChange={(o) => { setAssistantOpen(o); if (!o) setAssistantPanel(null); }}
              initialPanel={assistantPanel}
              onSuccess={loadLibrary}
              blocksCount={blocks.length}
              scope={{
                mode: modeFilter !== "all" ? modeFilter : "",
                tags: [...tagFilters],
                tagMatchAll,
                stage: stageFilter !== "all" ? stageFilter : "",
              }}
              initialMode={suggestInitial.mode}
              initialCategory={suggestInitial.category}
            />
          </div>
        </div>

        {/* Library summary bar */}
        {!loading && blocks.length > 0 && (
          <StatBar
            testId="library-summary"
            stats={[
              { label: "blocks", value: blocks.length },
              { label: "modes covered", value: Object.keys(modeCounts).length },
              { label: "favorites", value: blocks.filter((b) => b.is_favorite).length },
              { label: "categories", value: Object.keys(tagCounts).length },
            ]}
          />
        )}

      </div>{/* end fixed top */}

      {/* Scrollable content area */}
      <div className="flex-1 overflow-hidden max-w-[1400px] w-full mx-auto px-6">
        <div className={`grid h-full gap-4 ${showFilters ? "grid-cols-1 lg:grid-cols-12" : "grid-cols-1"}`}>
          {/* Left Sidebar — Filter Panel */}
          <aside className={`shrink-0 transition-all duration-200 ${showFilters ? "lg:col-span-3" : "hidden"}`} data-testid="library-sidebar">
            <div className="space-y-3 overflow-y-auto h-full pb-4">

              {/* Mode pills */}
              <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-3" data-testid="library-mode-nav">
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] block mb-2">Mode</span>
                <div className="flex flex-wrap gap-1" data-testid="library-mode-filter">
                  {MODE_FILTERS.map((m) => {
                    const count = m === "all" ? blocks.length : (modeCounts[m] || 0);
                    const isActive = modeFilter === m;
                    return (
                      <button key={m} onClick={() => setModeFilter(m)} data-testid={`lib-mode-${m}`}
                        className={`text-[11px] px-2 py-1 font-medium transition-colors ${isActive ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                        {m === "all" ? "All" : m.charAt(0).toUpperCase() + m.slice(1)} <span className="opacity-60">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category tag pills */}
              <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-3" data-testid="library-category-nav">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)]">Category</span>
                  {tagFilters.size > 1 && (
                    <button
                      onClick={() => setTagMatchAll((v) => !v)}
                      data-testid="lib-tag-match-toggle"
                      title={tagMatchAll ? "Currently matching blocks that have ALL selected tags. Click to match ANY." : "Currently matching blocks that have ANY selected tag. Click to match ALL."}
                      className="text-[10px] font-mono text-[var(--pf-text-muted)] hover:text-[var(--pf-accent)] flex items-center gap-1"
                    >
                      match:
                      <span className={`px-1.5 py-0.5 font-semibold ${tagMatchAll ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text)]"}`}>
                        {tagMatchAll ? "ALL" : "ANY"}
                      </span>
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1" data-testid="library-tag-filter">
                  <button onClick={() => setTagFilters(new Set())} data-testid="lib-tag-all"
                    className={`text-[11px] px-2 py-1 font-medium transition-colors ${tagFilters.size === 0 ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                    All
                  </button>
                  {CATEGORY_TAGS.filter((t) => tagCounts[t]).map((t) => {
                    const isActive = tagFilters.has(t);
                    return (
                      <button key={t} onClick={() => toggleTagFilter(t)} data-testid={`lib-tag-${t.toLowerCase().replace(/\s/g, "-")}`}
                        className={`text-[11px] px-2 py-1 font-medium transition-colors ${isActive ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                        {t} <span className="opacity-60">{tagCounts[t]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Source + Favorites */}
              <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-3" data-testid="library-source-nav">
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] block mb-2">Source</span>
                <div className="flex flex-wrap gap-1">
                  {SOURCE_FILTERS.map((sf) => {
                    const isActive = sourceFilter === sf.value;
                    return (
                      <button key={sf.value} onClick={() => setSourceFilter(sf.value)} data-testid={`lib-source-${sf.value || "all"}`}
                        className={`text-[11px] px-2 py-1 font-medium transition-colors ${isActive ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                        {sf.label}
                      </button>
                    );
                  })}
                  <button onClick={() => setShowFavorites(!showFavorites)} data-testid="library-favorites-toggle"
                    className={`text-[11px] px-2 py-1 font-medium transition-colors flex items-center gap-1 ${showFavorites ? "bg-[#EAB308] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[#EAB308] hover:text-white"}`}>
                    <Star className={`w-3 h-3 ${showFavorites ? "fill-current" : ""}`} />Favorites
                  </button>
                </div>
              </div>

              {/* Stage (collapsed) */}
              <Collapsible>
                <CollapsibleTrigger className="w-full flex items-center justify-between px-3 py-2 border border-[var(--pf-border)] bg-[var(--pf-bg)] group" data-testid="stage-collapse-toggle">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)]">Stage</span>
                  <ChevronRight className="w-3 h-3 text-[var(--pf-text-muted)] transition-transform group-data-[state=open]:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border border-t-0 border-[var(--pf-border)] bg-[var(--pf-bg)] p-3" data-testid="library-stage-nav">
                    <div className="flex flex-wrap gap-1">
                      {STAGE_TABS.map(({ value, label, icon: Icon }) => {
                        const count = stageCounts[value] || 0;
                        const isActive = stageFilter === value;
                        return (
                          <button key={value} onClick={() => { setStageFilter(value); setSelectedBlocks(new Set()); }} data-testid={`lib-stage-${value}`}
                            className={`text-[11px] px-2 py-1 font-medium transition-colors flex items-center gap-1 ${isActive ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                            <Icon className="w-3 h-3" />{label} <span className="opacity-60">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Clear filters */}
              {activeFilterCount > 0 && (
                <button onClick={() => { setModeFilter("all"); setTagFilters(new Set()); setSourceFilter(""); setShowFavorites(false); setStageFilter("all"); }} data-testid="lib-clear-filters"
                  className="w-full text-[11px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium py-2 border border-dashed border-[var(--pf-border)] hover:border-[var(--pf-accent)] transition-colors text-center">
                  Clear {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
                </button>
              )}
            </div>
          </aside>

          {/* Main content */}
          <div className={`${showFilters ? "lg:col-span-9" : "col-span-1"} overflow-y-auto h-full pb-4`}>
            {/* Search bar + toggle + sort */}
            <div className="flex items-center gap-3 mb-3 sticky top-0 z-10 bg-[var(--pf-bg)] py-2">
              <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters"
                className="flex items-center gap-1.5 text-xs font-medium text-[var(--pf-text-secondary)] hover:text-[var(--pf-accent)] transition-colors px-2 py-1.5 border border-[var(--pf-border)] hover:border-[var(--pf-accent)] shrink-0">
                <Filter className="w-3.5 h-3.5" />
                {showFilters ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {activeFilterCount > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-accent)] text-white">{activeFilterCount}</span>}
              </button>

              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--pf-text-muted)]" />
                <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search blocks by title, content, or tag..." data-testid="library-search"
                  className="rounded-none border-[var(--pf-border)] h-9 text-xs pl-8 focus:ring-2 focus:ring-[var(--pf-accent)]" />
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {/* Sort dropdown */}
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="rounded-none border-[var(--pf-border)] h-9 text-xs w-[140px]" data-testid="sort-select">
                    <ArrowUpDown className="w-3 h-3 mr-1.5 text-[var(--pf-text-muted)]" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-[var(--pf-border)]">
                    {SORT_OPTIONS.map((opt) => (<SelectItem key={opt.value} value={opt.value} className="rounded-none text-xs">{opt.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                {filteredBlocks.length > 0 && (
                  <span className="text-[11px] text-[var(--pf-text-muted)]">{filteredBlocks.length} block{filteredBlocks.length !== 1 ? "s" : ""}</span>
                )}
                {filteredBlocks.length > 0 && (
                  <button onClick={handleSelectAll} data-testid="library-select-all" className="text-[11px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium">
                    {selectedBlocks.size === filteredBlocks.length ? "Deselect all" : "Select all"}
                  </button>
                )}
                {selectedBlocks.size > 0 && (
                  <Button size="sm" onClick={handleCopySelected} data-testid="library-copy-selected"
                    className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-8 text-[11px] font-medium px-2.5">
                    <Copy className="w-3 h-3 mr-1" />Copy {selectedBlocks.size}
                  </Button>
                )}
              </div>
            </div>

            {/* Active filter pills (inline summary) */}
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="active-filters">
                {stageFilter !== "all" && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-[var(--pf-accent-subtle,rgba(0,71,255,0.06))] text-[var(--pf-accent)] font-medium border border-[var(--pf-accent)]/20">
                    Stage: {stageFilter.charAt(0).toUpperCase() + stageFilter.slice(1)}
                    <button onClick={() => setStageFilter("all")} className="hover:text-[var(--pf-accent-hover)]"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {modeFilter !== "all" && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-[var(--pf-accent-subtle,rgba(0,71,255,0.06))] text-[var(--pf-accent)] font-medium border border-[var(--pf-accent)]/20">
                    Mode: {modeFilter.charAt(0).toUpperCase() + modeFilter.slice(1)}
                    <button onClick={() => setModeFilter("all")} className="hover:text-[var(--pf-accent-hover)]"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {tagFilters.size > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-[var(--pf-accent-subtle,rgba(0,71,255,0.06))] text-[var(--pf-accent)] font-medium border border-[var(--pf-accent)]/20">
                    <Tag className="w-3 h-3" />{[...tagFilters].join(", ")}
                    <button onClick={() => setTagFilters(new Set())} className="hover:text-[var(--pf-accent-hover)]"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {showFavorites && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-[rgba(234,179,8,0.08)] text-[#B8860B] font-medium border border-[#EAB308]/20">
                    <Star className="w-3 h-3 fill-[#EAB308]" />Favorites
                    <button onClick={() => setShowFavorites(false)} className="hover:text-[#8B6914]"><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
            )}

            {/* Block list */}
            {loading ? (
              <div className="space-y-3">{[1, 2, 3, 4].map((i) => (<div key={i} className="border border-[var(--pf-border)] p-4"><div className="h-3 w-1/3 bg-[var(--pf-surface)] skeleton-pulse mb-2" /><div className="h-3 w-full bg-[var(--pf-surface)] skeleton-pulse" /></div>))}</div>
            ) : filteredBlocks.length === 0 ? (
              <div className="border border-[var(--pf-border)] p-12 text-center" data-testid="library-empty">
                <Library className="w-8 h-8 text-[var(--pf-text-muted)] mx-auto mb-3" />
                <h3 className="font-heading text-lg font-bold mb-1 text-[var(--pf-text)]">{blocks.length === 0 ? "Library is empty" : "No matching blocks"}</h3>
                <p className="text-sm text-[var(--pf-text-secondary)] mb-4">{blocks.length === 0 ? "Install a starter pack or import your own prompts." : "Adjust filters or search."}</p>
                {blocks.length === 0 ? (
                  <Button onClick={() => { setAssistantPanel("ingest"); setAssistantOpen(true); }} className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-9 px-4 text-sm">
                    <Upload className="w-3.5 h-3.5 mr-1.5" />Import Your First Prompts
                  </Button>
                ) : activeFilterCount > 0 ? (
                  <Button onClick={resetAllFilters} data-testid="reset-filters-btn" variant="outline"
                    className="rounded-none border-[var(--pf-border)] h-9 px-4 text-sm hover:border-[var(--pf-accent)]">
                    <X className="w-3.5 h-3.5 mr-1.5" />Reset {activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""}
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="library-blocks-list">
                {filteredBlocks.map((block, idx) => {
                  const uid = getBlockUid(block, idx);
                  const quality = block.quality || "template";
                  const qualityBorder = QUALITY_BORDERS[quality] || QUALITY_BORDERS.template;
                  const isSelected = selectedBlocks.has(idx);
                  const isCopied = copiedId === uid;
                  const isStandalone = block.standalone;
                  const isExpanded = expandedBlock === uid;

                  return (
                    <div key={uid} className={`border border-[var(--pf-border)] border-l-4 ${qualityBorder} bg-[var(--pf-bg)] transition-colors duration-150 hover:border-[var(--pf-accent)] ${(block.stage || "draft") === "deprecated" ? "opacity-50" : ""}`} data-testid={`lib-block-${uid}`}>
                      {/* Collapsed row: always visible */}
                      <div className="flex items-center gap-3 px-4 py-2.5 cursor-pointer" onClick={() => setExpandedBlock(isExpanded ? null : uid)}>
                        <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); toggleSelect(idx); }}
                          className="w-3.5 h-3.5 rounded-none border-[var(--pf-border)] accent-[var(--pf-accent)] cursor-pointer shrink-0" data-testid={`lib-check-${idx}`} />

                        <div className="flex-1 min-w-0 flex items-center gap-2">
                          <span className="text-xs font-semibold text-[var(--pf-text)] uppercase tracking-[0.1em] truncate">{block.title}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-surface)] text-[var(--pf-text-muted)] shrink-0">{block.mode}</span>
                          {(block.tags || []).slice(0, 2).map((tag) => (
                            <button key={tag} onClick={(e) => { e.stopPropagation(); toggleTagFilter(tag); }} className={`text-[10px] px-1.5 py-0.5 border transition-colors shrink-0 ${tagFilters.has(tag) ? "bg-[var(--pf-accent)] text-white border-[var(--pf-accent)]" : "bg-[var(--pf-surface)] text-[var(--pf-text-muted)] border-[var(--pf-border)] hover:border-[var(--pf-accent)] hover:text-[var(--pf-accent)]"}`}>
                              {tag}
                            </button>
                          ))}
                          {block.is_favorite && <Star className="w-3 h-3 fill-[#EAB308] text-[#EAB308] shrink-0" />}
                          {block.reflection_count > 0 && (
                            <span
                              className={`text-[10px] px-1.5 py-0.5 border shrink-0 font-medium ${
                                block.success_rate >= 0.7
                                  ? "border-emerald-500 text-emerald-600 bg-emerald-50"
                                  : block.success_rate >= 0.4
                                  ? "border-amber-500 text-amber-600 bg-amber-50"
                                  : "border-red-500 text-red-600 bg-red-50"
                              }`}
                              title={`${block.success_count} worked / ${block.failure_count} failed across ${block.reflection_count} reflection${block.reflection_count === 1 ? '' : 's'}`}
                              data-testid={`block-success-rate-${uid}`}
                            >
                              {Math.round((block.success_rate || 0) * 100)}%
                            </span>
                          )}
                        </div>

                        {/* Always-visible actions */}
                        <div className="flex items-center gap-0.5 shrink-0">
                          {isStandalone && (
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleToggleFavorite(block.id); }} data-testid={`lib-fav-${uid}`}
                              className="h-7 px-1.5 rounded-none text-xs hover:bg-[var(--pf-surface)]">
                              <Star className={`w-3 h-3 ${block.is_favorite ? "fill-[#EAB308] text-[#EAB308]" : "text-[var(--pf-text-muted)]"}`} />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCopyBlock(block, uid); }} data-testid={`lib-copy-${uid}`}
                            className="h-7 px-1.5 rounded-none text-xs hover:bg-[var(--pf-surface)]">
                            {isCopied ? <Check className="w-3 h-3 text-[#16A34A]" /> : <Copy className="w-3 h-3 text-[var(--pf-text-muted)]" />}
                          </Button>
                          {isStandalone && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} data-testid={`lib-menu-${uid}`}
                                  className="h-7 px-1.5 rounded-none text-xs hover:bg-[var(--pf-surface)]">
                                  <MoreHorizontal className="w-3 h-3 text-[var(--pf-text-muted)]" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="rounded-none border-[var(--pf-border)] w-44" onClick={(e) => e.stopPropagation()}>
                                {(block.stage || "draft") === "draft" && <DropdownMenuItem onClick={() => handleStageChange(block.id, "tested")} className="rounded-none text-xs cursor-pointer"><TestTube className="w-3 h-3 mr-2" />Mark as Tested</DropdownMenuItem>}
                                {(block.stage || "draft") === "tested" && <DropdownMenuItem onClick={() => handleStageChange(block.id, "production")} className="rounded-none text-xs cursor-pointer"><Shield className="w-3 h-3 mr-2" />Promote to Production</DropdownMenuItem>}
                                {(block.stage || "draft") === "production" && <DropdownMenuItem onClick={() => handleStageChange(block.id, "tested")} className="rounded-none text-xs cursor-pointer"><TestTube className="w-3 h-3 mr-2" />Demote to Tested</DropdownMenuItem>}
                                {(block.stage || "draft") !== "deprecated" && <DropdownMenuItem onClick={() => handleStageChange(block.id, "deprecated")} className="rounded-none text-xs cursor-pointer text-[var(--pf-text-muted)]"><Archive className="w-3 h-3 mr-2" />Deprecate</DropdownMenuItem>}
                                {(block.stage || "draft") === "deprecated" && <DropdownMenuItem onClick={() => handleStageChange(block.id, "draft")} className="rounded-none text-xs cursor-pointer"><FileText className="w-3 h-3 mr-2" />Restore as Draft</DropdownMenuItem>}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setDeleteConfirm({ id: block.id, title: block.title })} className="rounded-none text-xs cursor-pointer text-red-500"><Trash2 className="w-3 h-3 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          <ChevronDown className={`w-3.5 h-3.5 text-[var(--pf-text-muted)] transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        </div>
                      </div>

                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="px-4 pb-3 pt-0 border-t border-[var(--pf-border)]">
                          {editingBlock === block.id ? (
                            <div className="space-y-3 py-3" data-testid={`lib-edit-form-${uid}`}>
                              <div>
                                <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] block mb-1">Title</label>
                                <input value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                                  className="w-full h-8 px-2 text-xs font-semibold uppercase tracking-[0.1em] border border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)] focus:ring-2 focus:ring-[var(--pf-accent)] focus:outline-none" data-testid="edit-title" />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] block mb-1">Content</label>
                                <textarea value={editForm.content} onChange={(e) => setEditForm((f) => ({ ...f, content: e.target.value }))}
                                  className="w-full px-2 py-2 text-sm font-mono border border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)] focus:ring-2 focus:ring-[var(--pf-accent)] focus:outline-none resize-none" rows={5} data-testid="edit-content" />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] block mb-1">Mode</label>
                                  <Select value={editForm.mode} onValueChange={(v) => setEditForm((f) => ({ ...f, mode: v }))}>
                                    <SelectTrigger className="rounded-none border-[var(--pf-border)] h-8 text-xs" data-testid="edit-mode"><SelectValue /></SelectTrigger>
                                    <SelectContent className="rounded-none border-[var(--pf-border)]">
                                      {MODE_FILTERS.filter((m) => m !== "all").map((m) => (<SelectItem key={m} value={m} className="rounded-none text-xs">{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] block mb-1">Tags (comma separated)</label>
                                  <input value={editForm.tags} onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))}
                                    className="w-full h-8 px-2 text-xs border border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)] focus:ring-2 focus:ring-[var(--pf-accent)] focus:outline-none" placeholder="UI, Auth, API" data-testid="edit-tags" />
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button onClick={handleEditBlock} disabled={editSaving} data-testid="edit-save"
                                  className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-8 px-4 text-xs font-medium">
                                  {editSaving ? "Saving..." : "Save Changes"}
                                </Button>
                                <Button variant="outline" onClick={() => setEditingBlock(null)} data-testid="edit-cancel"
                                  className="rounded-none border-[var(--pf-border)] h-8 px-4 text-xs">Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="font-mono text-sm leading-relaxed text-[var(--pf-text)] whitespace-pre-wrap py-3">{block.content}</p>
                              <div className="flex items-center gap-2 flex-wrap">
                                {(block.tags || []).map((tag) => (
                                  <button key={tag} onClick={() => toggleTagFilter(tag)} className={`text-[10px] px-1.5 py-0.5 border transition-colors cursor-pointer ${tagFilters.has(tag) ? "bg-[var(--pf-accent)] text-white border-[var(--pf-accent)]" : "bg-[var(--pf-surface)] text-[var(--pf-text-muted)] border-[var(--pf-border)] hover:border-[var(--pf-accent)] hover:text-[var(--pf-accent)]"}`} data-testid={`block-tag-${tag.toLowerCase().replace(/\s/g, "-")}`}>
                                    {tag}
                                  </button>
                                ))}
                                <span className={`text-[10px] px-1.5 py-0.5 font-medium ${STAGE_COLORS[block.stage || "draft"] || STAGE_COLORS.draft}`}>{block.stage || "draft"}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 font-medium ${SOURCE_BADGES[block.source || "project"] || SOURCE_BADGES.project}`}>{block.source || "project"}</span>
                                {block.build_type && block.build_type !== "general" && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-surface)] text-[var(--pf-text-muted)]">{block.build_type}</span>
                                )}
                                {isStandalone && (
                                  <button onClick={() => startEditing(block, uid)} data-testid={`lib-edit-${uid}`}
                                    className="text-[10px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium flex items-center gap-0.5 ml-auto">
                                    Edit
                                  </button>
                                )}
                                {!isStandalone && block.project_id && (
                                  <button onClick={() => navigate(`/projects/${block.project_id}`)} className="text-[10px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium flex items-center gap-0.5">
                                    <ExternalLink className="w-3 h-3" />Go to project
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>{/* end main content */}
        </div>{/* end grid */}
      </div>{/* end scrollable area */}


      {/* P1.2: Audit / Suggest / Import are now unified inside the
          LibraryAssistant Sheet, mounted in the page header. The standalone
          Dialog components remain exported for backward compat / direct use,
          but are no longer mounted on this page. */}
      {(() => { const _scope = { mode: modeFilter !== "all" ? modeFilter : "", tags: [...tagFilters], tagMatchAll, stage: stageFilter !== "all" ? stageFilter : "" }; void _scope; return null; })()}

      {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
          <DialogContent className="rounded-none border-[var(--pf-border)] sm:max-w-sm">
<DialogBody>
            <DialogHeader><DialogTitle className="font-heading text-lg font-bold">Delete Block</DialogTitle></DialogHeader>
            <p className="text-sm text-[var(--pf-text-secondary)] mb-4">
              Delete &quot;{deleteConfirm?.title}&quot;? This cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 rounded-none border-[var(--pf-border)] h-9" data-testid="delete-cancel">Cancel</Button>
              <Button onClick={() => handleDeleteBlock(deleteConfirm?.id)} className="flex-1 rounded-none bg-red-600 hover:bg-red-700 text-white h-9" data-testid="delete-confirm">Delete</Button>
            </div>
</DialogBody>
</DialogContent>
        </Dialog>
    </div>
  );
}

